(function () {
  'use strict';

  class GameAPIClient {
    constructor() {
      this.maxRetries = 3;
      this.baseRetryDelay = 1000;
      this.bannedPhrases = [
        'google',
        'language model',
        'openai',
        'gpt',
        'gpt-3',
        'gpt3',
        'ai language',
        'artificial intelligence',
        'trained',
        'development',
        'bard',
        'human-like',
        'feedback',
        'sorry',
        'apologize',
        'i will not',
        'under development',
        'explicit content',
        'not appropriate',
        'not capable',
        'programmed',
        'inappropriate',
        'refrain',
        'as a',
        "i don't",
      ];
    }

    async fetchChatPrediction(sentText, context, examples, options = {}) {
      const {
        selectedModel = $('#ai-model-selector').val(),
        currentUserName = this.getCurrentUserName(),
        isRoomMode = !!(window.fixtures && window.fixtures.room_name),
        numRetries = 0,
      } = options;

      const retryConfig = {
        maxRetries: this.maxRetries,
        retryDelay: this.baseRetryDelay,
        exponentialBackoff: true,
        shouldRetry: (error) => {
          if (error.message && error.message.includes('BANNED_RESPONSE')) {
            return false;
          }
          return true;
        },
        onRetry: (error, attempt, maxRetries) => {
          console.log(
            `[GameAPI] Retry attempt ${attempt}/${maxRetries} for chat prediction`
          );
          trackEvent('api', 'retry', `chat_prediction_${attempt}`);
        },
      };

      const endpoints = this.getChatEndpoints(numRetries, isRoomMode);

      for (let i = 0; i < endpoints.length; i++) {
        const endpoint = endpoints[i];

        try {
          const result = await retryUtils.withRetry(async () => {
            return this.callChatEndpoint(endpoint, {
              sentText,
              context,
              examples,
              selectedModel,
              currentUserName,
              isRoomMode,
            });
          }, retryConfig);

          if (result && this.isValidResponse(result)) {
            return result;
          }
        } catch (error) {
          console.warn(
            `[GameAPI] Endpoint ${endpoint.name} failed:`,
            error.message
          );

          if (i === endpoints.length - 1) {
            throw error;
          }
        }
      }

      throw new Error('All chat endpoints failed');
    }

    getChatEndpoints(numRetries, isRoomMode) {
      const endpoints = [];

      if (numRetries === 0 && !isRoomMode) {
        endpoints.push({
          name: 'openrouter',
          url: '/api/chatpred_or',
          method: 'POST',
        });
      }

      if (numRetries <= 1) {
        endpoints.push({
          name: 'netwrck_predictions',
          url: '/api/predictions',
          method: 'POST',
        });
      }

      endpoints.push({
        name: 'chatpred',
        url: '/api/chatpred',
        method: 'POST',
      });

      if (numRetries > 3) {
        endpoints.push({
          name: 'external_api',
          url: 'https://api.textgeneration.co/api/generate',
          method: 'POST',
        });
      }

      return endpoints;
    }

    async callChatEndpoint(endpoint, params) {
      const {
        sentText,
        context,
        examples,
        selectedModel,
        currentUserName,
        isRoomMode,
      } = params;

      let requestBody;
      let headers = { 'Content-Type': 'application/json' };

      switch (endpoint.name) {
        case 'openrouter':
          requestBody = {
            query: sentText,
            context: context,
            examples: examples,
            greeting: this.sanitizeGreeting(currentUserName),
            model_name: selectedModel,
            username: currentUserName,
          };
          break;

        case 'netwrck_predictions':
          requestBody = this.buildNetwrckPredictionBody(sentText, isRoomMode);
          headers['X-Rapid-API-Key'] = 'YHWEFVNWHXIWNGVSH';
          break;

        case 'chatpred':
          requestBody = {
            query: sentText,
            context: context,
            examples: examples,
            greeting: this.sanitizeGreeting(currentUserName),
            model_name: selectedModel,
            username: currentUserName,
          };
          break;

        case 'external_api':
          requestBody = this.buildExternalAPIBody(sentText);
          headers['X-Rapid-API-Key'] = 'YHWEFVNWHXIWNGVSH';
          break;

        default:
          throw new Error(`Unknown endpoint: ${endpoint.name}`);
      }

      const response = await fetch(endpoint.url, {
        method: endpoint.method,
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return this.processResponse(data, endpoint.name);
    }

    processResponse(data, endpointName) {
      let responseText = '';

      if (typeof data === 'string') {
        responseText = data;
      } else if (data && data.generated_text) {
        responseText = data.generated_text;
      } else if (Array.isArray(data) && data[0] && data[0].generated_text) {
        responseText = data[0].generated_text;
      } else {
        throw new Error('Unexpected response format');
      }

      if (this.containsBannedPhrases(responseText)) {
        throw new Error('BANNED_RESPONSE: Response contains banned phrases');
      }

      return [
        {
          generated_text: responseText,
        },
      ];
    }

    containsBannedPhrases(text) {
      if (!text) return false;

      const lowerText = text.toLowerCase();
      return this.bannedPhrases.some((phrase) => lowerText.includes(phrase));
    }

    isValidResponse(result) {
      if (!result || !Array.isArray(result) || result.length === 0) {
        return false;
      }

      const response = result[0];
      if (!response || !response.generated_text) {
        return false;
      }

      const text = response.generated_text.trim();
      return text.length > 0;
    }

    getCurrentUserName() {
      if (window.userData) {
        return window.userData &&
          (window.userData?.username || window.userData?.name)
          ? window.userData?.username || window.userData?.name
          : 'User';
      }
      return 'User';
    }

    sanitizeGreeting(userName) {
      if (!window.fixtures || !window.fixtures.currentAI) {
        return '';
      }

      const greeting = window.fixtures.currentAI.greeting || '';
      return nlpUtils.sanitizePrompt(greeting).replace('{{user}}', userName);
    }

    buildNetwrckPredictionBody(sentText, isRoomMode) {
      const data = this.getGameData();

      const inputsCopy = JSON.parse(JSON.stringify(data));
      inputsCopy.stop_sequences = inputsCopy.stop_sequences || [];
      inputsCopy.stop_sequences.push('User 0:', 'User 1:');

      if (isRoomMode && window.questionsgame && window.questionsgame.game) {
        const characters = new Set();
        const lastCharacters =
          window.questionsgame.game.allMessagesCharacters.slice(-6);

        lastCharacters.forEach((character) => {
          if (character) {
            characters.add(character + ':');
          }
        });

        inputsCopy.stop_sequences.push(...Array.from(characters));
        inputsCopy.stop_sequences = [...new Set(inputsCopy.stop_sequences)];
      }

      return inputsCopy;
    }

    buildExternalAPIBody(sentText) {
      return this.getGameData();
    }

    getGameData() {
      if (!window.questionsgame || !window.questionsgame.game) {
        throw new Error('Game state not available');
      }

      return {
        text: window.questionsgame.game.input || '',
        stop_sequences: [],
        max_tokens: 100,
        temperature: 0.8,
      };
    }
  }

  window.gameAPIClient = new GameAPIClient();

  window.GameAPIClient = GameAPIClient;
})();
